import { create } from "zustand";
import {
  register,
  SIGNUP_FLOW_EVENTS,
  SIGNUP_FLOW_VERSION,
  SIGNUP_FLOW_VERSION_PROP,
  track,
} from "@/features/analytics";
import { useAuthStore } from "@/features/authentication";
import type { CreateTarget } from "@/features/standalone-versions";
import { isStandaloneVersionsOn } from "@/features/standalone-versions/use-standalone-versions";
import { FUNNEL_PATHS } from "./funnel-paths";
import { clearDescribeDraft } from "./store/describe-draft";
import { resetBrandPrefill } from "./store/brand-prefill.store";

/** Coarse entry-point label for the platform funnel, from how it was opened. */
const resolveEntryPoint = (initialPath: string, initialState: unknown) => {
  if (
    initialState &&
    typeof initialState === "object" &&
    "category" in initialState
  )
    return "sidebar shortcut";
  return initialPath === FUNNEL_PATHS.root ? "create button" : "dashboard cta";
};

interface PlatformFunnelState {
  isOpen: boolean;
  /** Funnel step to open at (in-memory funnel path), e.g. "/pages/wizard/ai/type". */
  initialPath: string;
  /** Location state seeded onto the opening step (e.g. a preselected category). */
  initialState: unknown;
  /**
   * What the run ends in: a story, or a standalone version (see
   * features/standalone-versions). Read by the completer, which is outside
   * the funnel's own tree.
   */
  target: CreateTarget;
  openFunnel: (
    initialPath?: string,
    initialState?: unknown,
    options?: { target?: CreateTarget },
  ) => void;
  closeFunnel: () => void;
}

/**
 * Same roles blocked from the protected `wizard/*` route via RoleGuard --
 * except that a Creator lite, who may not create stories, MAY run the funnel
 * to make a version: that is the one document of their own they can have.
 */
export const isFunnelRoleBlocked = (
  target: CreateTarget = "story",
): boolean => {
  const { isUser, isCreatorLite, isUserOrCreatorLite } =
    useAuthStore.getState();
  // Without standalone versions the funnel is the creators' alone, as it was.
  if (!isStandaloneVersionsOn()) return isUserOrCreatorLite;
  if (isUser) return true;
  return isCreatorLite && target !== "version";
};

/**
 * Controls the logged-in "how to start" funnel overlay on the dashboard. The
 * dashboard renders `<PlatformFunnelOverlay>`; dashboard CTAs call `openFunnel`.
 */
export const usePlatformFunnelStore = create<PlatformFunnelState>((set) => ({
  isOpen: false,
  initialPath: FUNNEL_PATHS.root,
  initialState: null,
  target: "story",
  openFunnel: (
    initialPath = FUNNEL_PATHS.root,
    initialState = null,
    options = {},
  ) => {
    const target = options.target ?? "story";
    // Mirror RoleGuard on /pages/wizard — USER must not enter, and
    // CREATOR_LITE only to make a version.
    if (isFunnelRoleBlocked(target)) return;
    // Each overlay open is a fresh run — drop any previous session draft and any
    // brand a previous run selected.
    clearDescribeDraft();
    resetBrandPrefill();
    // Logged-in sessions never hit the sign-up page, so tag the flow version
    // here too and fire the funnel-entry step (covers deep-linked entries that
    // bypass the first screen, e.g. sidebar document-type shortcuts).
    register({ [SIGNUP_FLOW_VERSION_PROP]: SIGNUP_FLOW_VERSION });
    track(SIGNUP_FLOW_EVENTS.START_CREATION_FLOW, {
      "entry point": resolveEntryPoint(initialPath, initialState),
    });
    set({ isOpen: true, initialPath, initialState, target });
  },
  closeFunnel: () => set({ isOpen: false }),
}));
